import React from 'react';
import { stripMarkdownFormatting } from '../utils/markdown';
import Pagination from '../components/Pagination';
import EmptyState from '../components/EmptyState';

const TopicArticlesList = ({
  topicId,
  articles,
  showAll,
  setShowAll,
  pagination,
  loading,
  onPageChange,
  onPerPageChange,
  onArticleClick,
  formatDate,
  formatSimilarity,
  getSimilarityColor,
}) => (
  <>
    {/* Filter Controls */}
    <div className="card-clay-static !p-2 sm:!p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center cursor-pointer group">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowAll(checked);
              sessionStorage.setItem(`topic-${topicId}-showAll`, checked.toString());
            }}
            className="rounded-clay border-clay-border text-primary-600 focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-body-text group-hover:text-primary-600 transition-colors">Show all</span>
        </label>
        <div className="text-sm text-secondary-text">
          {pagination ?
            `${Math.min((pagination.page - 1) * pagination.per_page + 1, pagination.total)}–${Math.min(pagination.page * pagination.per_page, pagination.total)} of ${pagination.total}` :
            `${articles.length} articles`
          }
        </div>
      </div>
    </div>

    {/* Articles List */}
    <div className="card-clay overflow-hidden">
      <div className="px-3 py-4 sm:p-6 border-b border-clay-border">
        <h2 className="font-display text-xl font-semibold text-heading">Articles in Topic</h2>
        <p className="text-secondary-text text-sm mt-1">
          Articles are ordered by similarity to the topic cluster center
        </p>
      </div>

      {articles.length === 0 ? (
        <EmptyState
          icon="📰"
          title="No articles found"
          description={showAll ? 'This topic has no articles' : 'No unread articles in this topic'}
          className="p-8 text-center"
        />
      ) : (
        <div className="divide-y divide-clay-border">
          {articles.map((article) => (
            <div
              key={article.id}
              onClick={() => onArticleClick(article.id)}
              className="px-3 py-4 sm:p-6 hover:bg-clay-bg cursor-pointer transition-all duration-200"
            >
              <div className="min-w-0">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-heading leading-6 group-hover:text-primary-600">
                    {article.title}
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className={`badge-fun px-2 py-1 rounded-clay text-xs font-medium ${getSimilarityColor(article.similarity)}`}>
                      {formatSimilarity(article.similarity)}% match
                    </span>
                    {!article.is_read && (
                      <span className="badge-fun-accent">
                        Unread
                      </span>
                    )}
                  </div>
                </div>

                {article.summary && (
                  <p className="text-secondary-text text-sm mb-3 line-clamp-2">
                    {stripMarkdownFormatting(article.summary)}
                  </p>
                )}

                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-secondary-text">
                  {article.author && (
                    <span>👤 {article.author}</span>
                  )}
                  {article.domain && (
                    <span>🌐 {article.domain}</span>
                  )}
                  <span>📅 {formatDate(article.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Pagination */}
    {pagination && pagination.pages > 1 && (
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        isLoading={loading}
        className="mt-8"
        itemLabel="articles"
      />
    )}
  </>
);

export default TopicArticlesList;
